// filepath: apps/web/src/features/admin/secrets/SecretImpactDialog.tsx
//
// FR-UI-ADM-10 AC-3 + AC-15 -- impact analysis dialog.
//
// Renders the impact report listing all effect configurations
// (http_endpoints + email_channels) referencing the secret_ref_id
// being deleted. The Delete button is gated by an explicit
// "I understand" checkbox (destructive-action confirmation pattern
// mirroring the FR-TEN-6 typed-slug gate).
//
// When the impact report is empty, the dialog renders a
// "safe to delete" surface and the Delete button is enabled by
// default (no references to confirm against).
//
// INV-9: every string is rendered as a React text node -- no
// dangerouslySetInnerHTML.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { SecretImpactReport, SecretMeta } from "./types";

interface SecretImpactDialogProps {
  secret: SecretMeta;
  impact: SecretImpactReport | null;
  isLoading: boolean;
  isDeleting: boolean;
  onConfirmDelete: () => void;
  onCancel: () => void;
  errorMessage: string | null;
}

function describeReference(
  kind: "http_endpoint" | "email_channel",
): "http_endpoint" | "email_channel" {
  // The discriminator is rendered verbatim via the i18n key, not
  // concatenated into the DOM. Returns the kind string as-is so
  // the test snapshot can match the canonical value.
  return kind;
}

export function SecretImpactDialog({
  secret,
  impact,
  isLoading,
  isDeleting,
  onConfirmDelete,
  onCancel,
  errorMessage,
}: SecretImpactDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);

  const hasReferences = impact !== null && impact.references.length > 0;
  const canDelete =
    !isLoading && !isDeleting && !hasReferences
      ? true
      : !isLoading && !isDeleting && acknowledged;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="impact-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="impact-dialog"
    >
      <div className="w-full max-w-lg rounded-md border border-border bg-background p-4 shadow-lg">
        <h2
          id="impact-dialog-title"
          className="text-sm font-semibold"
          data-testid="impact-dialog-title"
        >
          {t("admin.secrets.dialog.impact.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("admin.secrets.dialog.impact.subtitle", undefined, {
            name: secret.name,
          })}
        </p>

        <div className="mt-3">
          {isLoading ? (
            <div
              className="rounded border border-border bg-muted px-3 py-2 text-xs text-muted-foreground"
              data-testid="impact-loading"
            >
              {t("common.loading")}
            </div>
          ) : hasReferences ? (
            <div data-testid="impact-references">
              <p className="text-xs font-medium">
                {t("admin.secrets.dialog.impact.references_label", undefined, {
                  count: impact!.references.length,
                })}
              </p>
              <ul className="mt-2 space-y-1">
                {impact!.references.map((ref) => (
                  <li
                    key={`${ref.kind}-${ref.id}`}
                    className="rounded border border-border bg-muted px-3 py-2 text-xs"
                    data-testid="impact-reference-row"
                    data-ref-kind={describeReference(ref.kind)}
                  >
                    <span className="font-medium">{ref.name}</span>
                    <span className="ml-2 text-muted-foreground">
                      (
                      {t(
                        "admin.secrets.dialog.impact.kind_" +
                          describeReference(ref.kind),
                      )}{" "}
                      · {ref.locator})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div
              className="rounded border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-700"
              data-testid="impact-empty"
            >
              {t("admin.secrets.dialog.impact.safe_to_delete")}
            </div>
          )}
        </div>

        {hasReferences ? (
          <label className="mt-4 flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={isDeleting}
              data-testid="impact-acknowledge-checkbox"
              className="mt-0.5"
            />
            <span>{t("admin.secrets.dialog.impact.acknowledge_label")}</span>
          </label>
        ) : null}

        {errorMessage !== null && errorMessage.length > 0 ? (
          <div
            className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
            role="alert"
            data-testid="impact-error"
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded border border-border px-3 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="impact-cancel"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={!canDelete}
            className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="impact-confirm-delete"
          >
            {isDeleting ? t("common.loading") : t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}
